/**
 * DraftsList — drafts inbox list rendering (Phase X.3.1 -> X.3.4.12).
 *
 * X.3.4.7: each row links to the draft detail page
 * (/t/{slug}/ai-tools/drafts/{id}); the preview shows up to two lines
 * (line-clamp-2 instead of a single clipped line); rows get the warm
 * Peace palette (sage-tint divider, warm-cream hover, forest/white
 * chips). tenantSlug is passed in (the one consumer, AiToolsDrafts,
 * already holds it) rather than pulling useTenant() here, keeping the
 * component a pure presenter.
 *
 * X.3.4.12: each row gets a small red delete control so a draft can be
 * removed straight from the list without clicking through. The control
 * is a SIBLING of the row Link (never nested) so a click never triggers
 * navigation. Delete is a hard delete (irreversible), so the control
 * uses a lightweight inline confirm ("Delete? [Delete] [Cancel]") rather
 * than a one-click x. The mutation lives in the page (AiToolsDrafts):
 * DraftsList stays a presenter and only owns the ephemeral
 * which-row-is-confirming state. The repo-layer visibilityCondition
 * already gates the delete endpoint, so the control can appear on every
 * rendered row (any row visible here is a row this user may delete) and
 * is tool-agnostic (Care Management / SPICE / Intake / Email / Brand
 * Formatter rows all get it).
 */

import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { AiDraftSummary } from '@/lib/aiDrafts';

export interface DraftsListProps {
  drafts: readonly AiDraftSummary[];
  tenantSlug: string;
  /** Fire the delete mutation for a row. Owned by the page. */
  onDelete: (id: string) => void;
  /** The row whose delete is in flight (disables its buttons, shows pending). */
  deletingId?: string | null;
}

const TOOL_LABEL: Record<AiDraftSummary['tool_name'], string> = {
  assessment: 'Assessment',
  intake: 'Intake',
  email: 'Email',
  brand_formatter: 'Brand Formatter',
  med_list: 'Medication List',
};

/**
 * Where a row opens. A medication list is edited in its tool (the review
 * grid, the client link, the date), so its row goes straight there with the
 * draft to resume; every other tool opens the detail page.
 */
export function draftHref(tenantSlug: string, d: Pick<AiDraftSummary, 'id' | 'tool_name'>): string {
  if (d.tool_name === 'med_list') {
    return `/t/${tenantSlug}/ai-tools/med-list?draft=${encodeURIComponent(d.id)}`;
  }
  return `/t/${tenantSlug}/ai-tools/drafts/${d.id}`;
}

const STATUS_LABEL: Record<AiDraftSummary['status'], string> = {
  draft: 'Draft',
  finalized: 'Finalized',
  archived: 'Archived',
};

// White pill with forest text + sage-tint border, matching the billing
// block's category chip (EmailBillingBlock.tsx). Used for the tool chip
// and the 'draft' status chip.
const CHIP_STYLE = {
  backgroundColor: 'var(--color-paper)',
  borderColor: '#C9D6CF',
  color: 'var(--color-sage-800)',
} as const;

// X.3.4.8: muted Sage-Tint pill for the 'finalized' status chip so a
// finalized draft reads as visually distinct (settled) from an active
// draft. Also applied defensively to any legacy 'archived' row (those no
// longer reach the inbox after the listDraftsForUser status filter).
const MUTED_CHIP_STYLE = {
  backgroundColor: '#ECF1EE',
  borderColor: 'var(--color-sage-300)',
  color: 'var(--color-sage-400)',
} as const;

// X.3.4.12: the saturated Peace brand red (the pepper mark / SPICE knob,
// EmailModeToggle.tsx) reserved here for the destructive delete affordance.
const DELETE_RED = '#C71B23';

/**
 * Per-row delete control (X.3.4.12). Rendered as a sibling of the row
 * Link. Two visual states driven by the parent's `confirming` flag:
 *   - resting: a small red x. On hover-capable pointers it fades in only
 *     on row hover/focus to keep the list calm; on touch / keyboard it is
 *     always visible and always reachable (never hover-only).
 *   - confirming: "Delete? [Delete] [Cancel]". Focus moves to the
 *     destructive Delete on open and back to the x trigger on cancel.
 */
function DraftRowDelete({
  draftId,
  triggerLabel,
  confirming,
  deleting,
  onRequestConfirm,
  onCancel,
  onConfirm,
}: {
  draftId: string;
  triggerLabel: string;
  confirming: boolean;
  deleting: boolean;
  onRequestConfirm: () => void;
  onCancel: () => void;
  onConfirm: () => void;
}): ReactNode {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const prevConfirming = useRef(false);

  useEffect(() => {
    if (confirming && !prevConfirming.current) {
      confirmRef.current?.focus();
    } else if (!confirming && prevConfirming.current) {
      triggerRef.current?.focus();
    }
    prevConfirming.current = confirming;
  }, [confirming]);

  if (confirming) {
    return (
      <div
        className="flex shrink-0 items-center gap-1 self-center"
        onKeyDown={(e) => {
          if (e.key === 'Escape' && !deleting) {
            e.stopPropagation();
            onCancel();
          }
        }}
      >
        <span className="text-xs" style={{ color: 'var(--color-sage-400)' }}>
          Delete?
        </span>
        <button
          ref={confirmRef}
          type="button"
          onClick={onConfirm}
          disabled={deleting}
          aria-label="Confirm delete draft"
          data-testid={`draft-delete-confirm-${draftId}`}
          className="min-h-9 rounded-md px-2 py-1 text-xs font-semibold text-white disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
          style={{ backgroundColor: DELETE_RED }}
        >
          {deleting ? 'Deleting…' : 'Delete'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={deleting}
          aria-label="Cancel delete draft"
          data-testid={`draft-delete-cancel-${draftId}`}
          className="min-h-9 rounded-md px-2 py-1 text-xs text-ink-soft hover:text-ink disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500"
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <button
      ref={triggerRef}
      type="button"
      onClick={onRequestConfirm}
      aria-label={triggerLabel}
      data-testid={`draft-delete-${draftId}`}
      className={
        'min-h-9 shrink-0 self-center rounded-md px-2 py-1 text-lg leading-none ' +
        'hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 ' +
        // Always visible (touch + keyboard); on hover-capable pointers,
        // reveal only on row hover/focus-within so the list stays calm.
        'opacity-100 [@media(hover:hover)]:opacity-0 ' +
        '[@media(hover:hover)]:group-hover:opacity-100 ' +
        '[@media(hover:hover)]:group-focus-within:opacity-100'
      }
      style={{ color: DELETE_RED }}
    >
      {'✕'}
    </button>
  );
}

export function DraftsList({
  drafts,
  tenantSlug,
  onDelete,
  deletingId = null,
}: DraftsListProps): ReactNode {
  // Ephemeral presentation state: which row is showing the inline confirm.
  // One row at a time — opening another resets the first.
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  return (
    <ul className="divide-y divide-[#A3B8B0]/25" data-testid="drafts-list">
      {drafts.map((d) => (
        <li key={d.id} className="group flex items-start gap-1 py-1">
          <Link
            to={draftHref(tenantSlug, d)}
            className="block min-w-0 flex-1 -ml-2 pl-2 pr-2 py-2 rounded-md hover:bg-[#FAF7F2] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#52796F]"
            data-testid={`draft-row-${d.id}`}
          >
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className="text-xs font-medium px-2 py-0.5 rounded-full border"
                style={CHIP_STYLE}
              >
                {TOOL_LABEL[d.tool_name]}
              </span>
              <span
                className="text-xs font-medium px-2 py-0.5 rounded-full border"
                style={d.status === 'draft' ? CHIP_STYLE : MUTED_CHIP_STYLE}
              >
                {STATUS_LABEL[d.status]}
              </span>
              <span className="text-xs" style={{ color: 'var(--color-sage-300)' }}>
                {d.created_at.slice(0, 10)}
              </span>
              {/* X.3.4.11: author chip, present only on the owner all-view
                  (created_by_name is null on own-view). */}
              {d.created_by_name && (
                <span
                  className="text-xs font-medium px-2 py-0.5 rounded-full border"
                  style={MUTED_CHIP_STYLE}
                  data-testid={`draft-author-${d.id}`}
                  aria-label={`Created by ${d.created_by_name}`}
                >
                  {d.created_by_name}
                </span>
              )}
            </div>
            <p className="text-sm mt-1 line-clamp-2" style={{ color: 'var(--color-ink)' }}>
              {d.preview}
            </p>
          </Link>
          {/* X.3.4.12: delete control is a SIBLING of the Link (not nested),
              so clicking it never triggers row navigation. */}
          <DraftRowDelete
            draftId={d.id}
            triggerLabel={`Delete draft from ${TOOL_LABEL[d.tool_name]}, ${d.created_at.slice(0, 10)}`}
            confirming={confirmingId === d.id}
            deleting={deletingId === d.id}
            onRequestConfirm={() => setConfirmingId(d.id)}
            onCancel={() => setConfirmingId(null)}
            onConfirm={() => onDelete(d.id)}
          />
        </li>
      ))}
    </ul>
  );
}
